import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import {
  ChevronIcon,
  EmailDiscoveryForm,
  LoadingState,
  Notice,
  ProductLockup,
  SignInMethodList,
  traceButtonClass,
  type BeginSignInOptions,
} from '@traceio-org/auth-ui';
import { isPlatformApiError } from '@traceio-org/platform-client';
import { PRODUCTS, type LoginMethod, type LoginOptions, type ProductKey } from '@traceio-org/platform-contracts';
import { useBootstrap, useHub } from '../app/context';
import { DevSection, devNoticeText } from '../app/dev-identity';
import { readHubState, useDecideAndFollow, useLoad } from '../app/hooks';
import { DecideFailedNotice, HubFrame, PageHeading } from '../app/ui';
import { SigningInPage, rememberSigningInProduct } from '../app/signing-in';
import type { LoginNotice } from '../navigation/decision';
import { forcedProductOf, type HostState } from '../security/host';
import { readReturnTo } from '../security/return-path';

type OrganizationSsoMethod = Extract<LoginMethod, { type: 'organization-sso' }>;

const PROVIDER_NAMES: Record<OrganizationSsoMethod['provider'], string> = {
  microsoft: 'Microsoft',
  google: 'Google',
  saml: 'your company sign-in',
  oidc: 'your company sign-in',
};

/** Products listed on Trace's own sign-in page; Trace Admin has its own quiet staff link. */
const CHOOSER_PRODUCTS: readonly ProductKey[] = ['enterprise', 'brokers', 'carriers', 'agents', 'portal'];

/**
 * /login and /login/<product>. The product comes from the route only. A customer domain pins its own
 * product: any other login route on it moves to that product's route, keeping the query (returnTo).
 */
export function LoginPage({ productKey }: { productKey: ProductKey | null }) {
  const { host } = useBootstrap();
  const location = useLocation();
  const forced = forcedProductOf(host);
  if (forced && productKey !== forced) {
    return <Navigate to={{ pathname: `/login/${forced}`, search: location.search }} state={location.state} replace />;
  }
  return productKey ? <ProductLogin key={productKey} productKey={productKey} /> : <TraceLogin />;
}

function loginTitle(productKey: ProductKey, host: HostState): string {
  if (host.kind === 'customer-employee') return `Sign in to ${host.branding.displayName}`;
  if (host.kind === 'customer-portal') return `Sign in to ${host.portalName}`;
  return `Sign in to ${PRODUCTS[productKey].displayName}`;
}

function loginLead(productKey: ProductKey): string {
  switch (PRODUCTS[productKey].context) {
    case 'employee':
      return 'Use your work account.';
    case 'portal':
      return 'Access to the customer portal comes from an invitation from your logistics provider. Open the link in your invitation email, or use the email sign-in your provider set up for you.';
    case 'staff':
      return 'For Trace staff only. Multi-factor authentication is required.';
  }
}

/**
 * Sign-in methods a product may show. The portal never offers employee SSO (Microsoft, Google or an
 * organization connection); only the email sign-in configured for it.
 */
function methodsFor(productKey: ProductKey, methods: readonly LoginMethod[]): LoginMethod[] {
  if (PRODUCTS[productKey].context === 'portal') {
    return methods.filter((m) => m.type === 'passwordless-email' || m.type === 'password');
  }
  return [...methods];
}

/**
 * Trace Admin labels the staff connection as such. Social methods keep their own labels: platform-api
 * decides which connections staff may use, and the page never disguises one method as another.
 */
function staffLabel(method: LoginMethod): string | undefined {
  if (method.type === 'password' || method.type === 'passwordless-email') return 'Continue with Trace staff sign-in';
  return undefined;
}

function discoveryErrorMessage(error: unknown): string {
  if (isPlatformApiError(error) && (error.code === 'validation_failed' || error.code === 'bad_request')) {
    return 'Enter a valid work email address.';
  }
  if (isPlatformApiError(error) && error.code === 'rate_limited') {
    return 'Too many attempts. Wait a moment, then try again.';
  }
  return 'We could not check that address. Try again.';
}

function LoopNotice({ notice }: { notice: LoginNotice }) {
  if (notice.kind !== 'reauthenticate-loop') return null;
  return (
    <Notice tone="warning" live="assertive" title="We could not confirm the sign-in this workspace requires.">
      <p>Try again with the sign-in method of your organization, or use a different account.</p>
    </Notice>
  );
}

function ProductLogin({ productKey }: { productKey: ProductKey }) {
  const deps = useHub();
  const { host } = useBootstrap();
  const location = useLocation();
  const returnPath = useMemo(() => readReturnTo(location.search), [location.search]);
  const { notice, silentTried, silentFailed } = useMemo(() => readHubState(location.state), [location.state]);
  const { run: runDecide, failed: decideFailed } = useDecideAndFollow();
  const [options, retryOptions] = useLoad((signal) => deps.client.loginOptions(productKey, { signal }), [deps, productKey]);
  const loaded: LoginOptions | null = options.status === 'ready' ? options.data : null;
  const auth0 = deps.sessions.auth0;
  const idpReady = auth0 !== null && loaded?.identityProvider.configured === true;
  const devEnabled = loaded?.developmentIdentity.enabled === true;
  const [startError, setStartError] = useState<string | null>(null);

  const baseOptions = useMemo<Omit<BeginSignInOptions, 'connection' | 'organization'>>(
    () => ({ productKey, ...(returnPath ? { returnPath } : {}) }),
    [productKey, returnPath],
  );
  const beginSignIn = useCallback(
    async (signIn: BeginSignInOptions) => {
      if (!auth0) return;
      setStartError(null);
      rememberSigningInProduct(signIn.productKey);
      await auth0.beginSignIn(signIn);
    },
    [auth0],
  );
  const onStartError = useCallback((error: unknown) => {
    console.warn('account-hub: could not start sign-in', error);
    setStartError('We could not reach the sign-in service. Check your connection, then try again.');
  }, []);
  const afterDevSignIn = useCallback(() => runDecide(baseOptions), [runDecide, baseOptions]);

  const context = PRODUCTS[productKey].context;
  // Silent sign-in first (prompt none, a top-level redirect: no third-party cookies involved). Products and
  // Trace Admin send people here whenever their own tab has no session, also right after the hub sent them
  // there signed in, because the hub keeps tokens in memory only; without this every such trip showed this page
  // again and needed a second click (hosted dev, 2026-10-07). Auth0 answers from its own session, or the
  // callback comes back here with silentTried and the methods show. The customer portal too: platform-api answers a
  // session from another connection (an employee's Google sign-in) with a reauthentication through the portal's
  // email codes, never with the portal, so only a portal session is reused; without this, a person who had just
  // entered a code at an invitation was asked for a second one (dev, 2026-10-09). Never while a notice explains a
  // reauthentication.
  const likelySilent = auth0 !== null && !silentTried && !notice;
  const silentWanted = idpReady && likelySilent;
  // Set once the silent attempt settled without leaving the page (it could not start): only then may the methods
  // show. Derived synchronously from silentWanted, so the form never renders for a frame before the attempt
  // starts (the "Continue with Google" flash seen on hosted dev, 2026-10-07).
  const [silentSettled, setSilentSettled] = useState(false);
  const silentStarted = useRef(false);
  useEffect(() => {
    if (!silentWanted || !auth0 || silentStarted.current) return;
    silentStarted.current = true;
    rememberSigningInProduct(productKey);
    // beginSignIn only settles when the page did not leave (it could not start): then the methods show.
    auth0
      .beginSignIn({ ...baseOptions, prompt: 'none' })
      .catch((error: unknown) => console.warn('account-hub: silent sign-in did not start', error))
      .finally(() => setSilentSettled(true));
  }, [silentWanted, auth0, baseOptions, productKey]);
  // Trace Admin, after its silent attempt failed (no Auth0 session, or a session without MFA: staff always need
  // one): the page would only offer its one staff method, so continue with it at once, on the same steady screen,
  // and the person lands on Google or straight on Auth0's MFA prompt (hosted dev 2026-10-08: a Brokers sign-in,
  // then Admin, stopped on this page's button). Once per page view (the ref); the interactive attempt that follows
  // never comes back with silentFailed, so this cannot loop, and a failure of it shows the callback's error page.
  const autoMethod =
    context === 'staff' &&
    silentFailed === true &&
    !notice &&
    idpReady &&
    loaded?.methods.length === 1 &&
    (loaded.methods[0]!.type === 'google' || loaded.methods[0]!.type === 'microsoft')
      ? loaded.methods[0]
      : undefined;
  const autoWanted = auth0 !== null && context === 'staff' && silentFailed === true && !notice;
  const [autoSettled, setAutoSettled] = useState(false);
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoMethod || !auth0 || autoStarted.current) return;
    autoStarted.current = true;
    rememberSigningInProduct(productKey);
    auth0
      .beginSignIn({ ...baseOptions, connection: autoMethod.connection })
      .catch((error: unknown) => console.warn('account-hub: staff sign-in did not start', error))
      .finally(() => setAutoSettled(true));
  }, [autoMethod, auth0, baseOptions, productKey]);
  const devNotice = notice?.kind === 'reauthenticate' ? devNoticeText(notice, productKey) : undefined;

  // While Trace checks for an existing sign-in (options still loading for a page that will try silently, or the
  // silent attempt running), the person sees only the steady signing-in screen: no heading swap, no back link,
  // no methods. The methods appear only when a person action is really needed.
  if (
    (options.status === 'loading' && (likelySilent || autoWanted)) ||
    (silentWanted && !silentSettled) ||
    (autoMethod !== undefined && !autoSettled)
  ) {
    return <SigningInPage productKey={productKey} />;
  }

  return (
    <HubFrame productKey={productKey}>
      {/* Back to the product chooser, except on a customer domain, which serves its one product only. */}
      {forcedProductOf(host) === null ? (
        <Link className="trace-auth-back" to="/login">
          <ChevronIcon />
          Back to all products
        </Link>
      ) : null}
      <PageHeading title={loginTitle(productKey, host)} lead={loginLead(productKey)} />
      {notice ? <LoopNotice notice={notice} /> : null}
      {decideFailed ? <DecideFailedNotice onRetry={() => void runDecide(baseOptions)} /> : null}
      {startError ? (
        <Notice tone="error" live="assertive" title={startError} />
      ) : null}

      {options.status === 'loading' ? <LoadingState label="Loading sign-in options" /> : null}
      {options.status === 'error' ? (
        <Notice tone="error" live="assertive" title="We could not load the sign-in options.">
          <div className="trace-auth-stack">
            <p>Check your connection, then try again.</p>
            <button type="button" className={traceButtonClass('primary', { block: true })} onClick={retryOptions}>
              Try again
            </button>
          </div>
        </Notice>
      ) : null}

      {loaded && !idpReady ? (
        <Notice title="Sign-in is not configured in this environment.">
          <p>
            {devEnabled && deps.sessions.dev
              ? 'Use a development identity below to try Trace locally.'
              : 'The identity provider for this environment is not set up yet.'}
          </p>
        </Notice>
      ) : null}

      {loaded && idpReady && context === 'employee' ? (
        <EmployeeMethods
          productKey={productKey}
          options={loaded}
          baseOptions={baseOptions}
          beginSignIn={beginSignIn}
          onStartError={onStartError}
        />
      ) : null}
      {loaded && idpReady && context === 'portal' ? (
        <SignInMethodList
          methods={methodsFor(productKey, loaded.methods)}
          beginSignIn={beginSignIn}
          signInOptions={baseOptions}
          onError={onStartError}
        />
      ) : null}
      {loaded && idpReady && context === 'staff' ? (
        <SignInMethodList
          methods={loaded.methods}
          beginSignIn={beginSignIn}
          signInOptions={baseOptions}
          onError={onStartError}
          labelFor={staffLabel}
          label="Trace staff sign-in"
        />
      ) : null}

      {devNotice && !(devEnabled && deps.sessions.dev) ? <Notice tone="warning" title={devNotice} /> : null}
      <DevSection enabled={devEnabled} afterSignIn={afterDevSignIn} notice={devNotice} />
    </HubFrame>
  );
}

type DiscoveryState =
  | { kind: 'idle' }
  | { kind: 'checking'; email: string }
  | { kind: 'error'; email: string; message: string }
  | { kind: 'continuing'; email: string; method: OrganizationSsoMethod }
  | { kind: 'choose'; email: string; methods: LoginMethod[] };

interface EmployeeMethodsProps {
  productKey: ProductKey;
  options: LoginOptions;
  baseOptions: Omit<BeginSignInOptions, 'connection' | 'organization'>;
  beginSignIn: (options: BeginSignInOptions) => Promise<void>;
  onStartError: (error: unknown) => void;
}

/**
 * Employee products: work email discovery first (when enabled), then the configured Microsoft and Google
 * buttons. When discovery finds an organization that enforces SSO, the page says where it is going and
 * continues after a short visible pause that can be cancelled.
 */
function EmployeeMethods({ productKey, options, baseOptions, beginSignIn, onStartError }: EmployeeMethodsProps) {
  const deps = useHub();
  const [discovery, setDiscovery] = useState<DiscoveryState>({ kind: 'idle' });
  const cancelRef = useRef<HTMLButtonElement>(null);
  const chooseRef = useRef<HTMLHeadingElement>(null);

  async function discover(email: string) {
    setDiscovery({ kind: 'checking', email });
    try {
      const result = await deps.client.discover({ email, productKey });
      const enforced = result.ssoEnforced
        ? result.methods.find((m): m is OrganizationSsoMethod => m.type === 'organization-sso')
        : undefined;
      if (enforced) setDiscovery({ kind: 'continuing', email, method: enforced });
      else setDiscovery({ kind: 'choose', email, methods: result.methods });
    } catch (error) {
      setDiscovery({ kind: 'error', email, message: discoveryErrorMessage(error) });
    }
  }

  // Continue to the enforced organization SSO after the visible pause, unless cancelled first.
  useEffect(() => {
    if (discovery.kind !== 'continuing') return;
    cancelRef.current?.focus();
    const { method, email } = discovery;
    const timer = window.setTimeout(() => {
      beginSignIn({ ...baseOptions, organization: method.organizationRef, connection: method.connection, loginHint: email }).catch(
        (error: unknown) => {
          setDiscovery({ kind: 'idle' });
          onStartError(error);
        },
      );
    }, deps.timing.ssoContinueDelayMs);
    return () => window.clearTimeout(timer);
  }, [discovery, baseOptions, beginSignIn, onStartError, deps.timing.ssoContinueDelayMs]);

  useEffect(() => {
    if (discovery.kind === 'choose') chooseRef.current?.focus();
  }, [discovery.kind]);

  if (discovery.kind === 'continuing') {
    return (
      <Notice
        title={`Continuing to ${PROVIDER_NAMES[discovery.method.provider]}`}
      >
        <div className="trace-auth-stack">
          <p>{discovery.email} signs in through its organization.</p>
          <button
            ref={cancelRef}
            type="button"
            className={traceButtonClass('link')}
            onClick={() => setDiscovery({ kind: 'idle' })}
          >
            Cancel
          </button>
        </div>
      </Notice>
    );
  }

  if (discovery.kind === 'choose') {
    return (
      <section className="trace-auth-stack" aria-labelledby="choose-method-heading">
        <div className="trace-auth-header">
          <h2 id="choose-method-heading" ref={chooseRef} tabIndex={-1} className="trace-auth-label">
            Choose how to sign in
          </h2>
          <p className="trace-auth-lead">{discovery.email}</p>
        </div>
        <SignInMethodList
          methods={discovery.methods}
          beginSignIn={beginSignIn}
          signInOptions={{ ...baseOptions, loginHint: discovery.email }}
          onError={onStartError}
        />
        <button type="button" className={traceButtonClass('link')} onClick={() => setDiscovery({ kind: 'idle' })}>
          Use a different email
        </button>
      </section>
    );
  }

  const showForm = options.emailDiscovery;
  const methods = methodsFor(productKey, options.methods);
  return (
    <>
      {showForm ? (
        <EmailDiscoveryForm
          onSubmit={discover}
          busy={discovery.kind === 'checking'}
          error={discovery.kind === 'error' ? discovery.message : null}
          defaultEmail={discovery.kind === 'error' ? discovery.email : ''}
        />
      ) : null}
      {showForm && methods.length > 0 ? <p className="trace-auth-divider">or</p> : null}
      <SignInMethodList methods={methods} beginSignIn={beginSignIn} signInOptions={baseOptions} onError={onStartError} />
    </>
  );
}

/** Trace's own /login: choose a product, or use a development identity and let Trace decide. */
function TraceLogin() {
  const deps = useHub();
  const { run: runDecide, failed: decideFailed } = useDecideAndFollow();
  const afterDevSignIn = useCallback(() => runDecide({}), [runDecide]);
  return (
    <HubFrame productKey={null}>
      <PageHeading title="Sign in to Trace" lead="Choose the Trace product you use." />
      {decideFailed ? <DecideFailedNotice onRetry={() => void runDecide({})} /> : null}
      <ul className="trace-auth-choices" aria-label="Trace products">
        {CHOOSER_PRODUCTS.map((key) => (
          <li key={key}>
            <Link className="trace-auth-choice trace-auth-choice--product" to={`/login/${key}`}>
              <span className="trace-auth-choice__lockup">
                <ProductLockup productKey={key} />
              </span>
              <ChevronIcon />
            </Link>
          </li>
        ))}
      </ul>
      {/* Not a product: the hub's own pages (members, roles, billing) for owners, administrators and billing
          administrators. /settings signs the person in (silently when an Auth0 session exists) and lands on their
          organization, or on the list when there are several. Hidden in production until billing is launched
          (Noam, 2026-10-14): the pages stay reachable from inside the hub, only this public entry is left out. */}
      {deps.config.environment === 'production' ? null : (
        <p className="trace-auth-muted">
          Your organization:{' '}
          <Link className="trace-auth-link" to="/settings">
            Account &amp; billing
          </Link>
        </p>
      )}
      <p className="trace-auth-muted">
        Trace staff:{' '}
        <Link className="trace-auth-link" to="/login/admin">
          sign in to Trace Admin
        </Link>
      </p>
      <DevSection enabled probe afterSignIn={afterDevSignIn} />
    </HubFrame>
  );
}
